/* PrintImageGuardModal — before printing a recipe (or a cookbook), check whether
 * any recipe has a video but no still image, and if so prompt the user to upload
 * one. The video stays attached for digital viewing; the new still goes into the
 * printed page where the video can't.
 *
 * Why not auto-generate?
 *   - YouTube poster frames (img.youtube.com/.../hqdefault.jpg) are 480×360 — too
 *     low-res for print quality; they also often show a person, not the dish.
 *   - Frames pulled from raw <video> elements vary wildly by encoder and many CDN-
 *     hosted videos don't expose CORS-readable frames in the browser.
 *   - Letting the user pick a representative photo guarantees a print-ready cover.
 *
 * Usage:
 *   const recipesNeedingImages = recipes.filter(r => r.video_url && (!r.images || r.images.length === 0));
 *   <PrintImageGuardModal open={open} recipes={recipesNeedingImages} onClose={...} onAllResolved={...} />
 */
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadSimple, X, CheckCircle, Warning } from "@phosphor-icons/react";

export default function PrintImageGuardModal({ open, recipes, onClose, onAllResolved }) {
  const [uploadingId, setUploadingId] = useState(null);
  const [resolved, setResolved] = useState(() => new Set());

  if (!open) return null;

  const handleUpload = async (recipe, file) => {
    if (!file) return;
    setUploadingId(recipe.recipe_id);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      // PATCH the recipe to add the new image as the first/hero
      const newImages = [res.url, ...(recipe.images || [])].slice(0, 6);
      await api.patch(`/recipes/${recipe.recipe_id}`, { images: newImages });
      setResolved((prev) => {
        const next = new Set(prev);
        next.add(recipe.recipe_id);
        return next;
      });
      toast.success(`Photo added to "${recipe.title}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const allResolved = recipes.every((r) => resolved.has(r.recipe_id));
  const remainingCount = recipes.filter((r) => !resolved.has(r.recipe_id)).length;

  return (
    <div className="fixed inset-0 z-[60] bg-rf-ink/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" onClick={onClose}>
      <div
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="print-image-guard-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-3 border-b border-rf-ink/10">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-rf-amber/30 flex items-center justify-center shrink-0">
              <Warning size={22} weight="duotone" className="text-rf-orange" />
            </div>
            <div>
              <h2 className="font-display font-black text-rf-ink text-xl leading-tight">Add a photo before printing</h2>
              <p className="text-rf-ink-soft text-sm mt-1">
                {recipes.length === 1
                  ? "This recipe only has a video. Printed pages can't show videos — please add a photo so the print looks great."
                  : `${recipes.length} recipes in this cookbook only have videos. Printed pages can't show videos — please add a photo for each so the printed book looks great.`}
                {" "}The video stays attached for digital use.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-rf-ink-soft hover:text-rf-ink" data-testid="print-image-guard-close">
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* List of recipes needing images */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {recipes.map((r) => {
            const done = resolved.has(r.recipe_id);
            const busy = uploadingId === r.recipe_id;
            return (
              <div
                key={r.recipe_id}
                data-testid={`print-guard-recipe-${r.recipe_id}`}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 ${done ? "bg-emerald-50 border-emerald-300" : "bg-rf-cream-alt border-rf-ink/10"}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-display font-extrabold text-rf-ink truncate">{r.title}</p>
                  <p className="text-xs text-rf-ink-soft">
                    {done ? "Photo added ✓" : busy ? "Uploading…" : "Video-only — needs a print photo"}
                  </p>
                </div>
                {done ? (
                  <CheckCircle size={26} weight="fill" className="text-emerald-600 shrink-0" />
                ) : (
                  <label className={`btn-amber !py-2 !px-3 !text-sm whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
                    <UploadSimple size={14} weight="bold" /> {busy ? "…" : "Add photo"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      data-testid={`print-guard-upload-${r.recipe_id}`}
                      onChange={(e) => handleUpload(r, e.target.files?.[0])}
                    />
                  </label>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-rf-ink/10 p-6 flex flex-wrap gap-3 justify-between items-center">
          <p className="text-xs text-rf-ink-soft">
            {allResolved
              ? "All photos added. Click continue to print."
              : `${remainingCount} ${remainingCount === 1 ? "recipe" : "recipes"} still need a photo.`}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              data-testid="print-image-guard-cancel"
              className="btn-outline-ink !py-2.5 !px-4 text-sm"
            >
              Cancel print
            </button>
            <button
              onClick={onAllResolved}
              disabled={!allResolved}
              data-testid="print-image-guard-continue"
              className="btn-amber !py-2.5 !px-4 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue to print →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
